import { arrowHead } from '../model/arrow';
import type { BoardData } from '../model/board';
import { DIRECTION_INDEX, type Direction } from '../model/direction';

const DIR_UP = 0;
const DIR_RIGHT = 1;
const DIR_DOWN = 2;
// Left needs no constant: it is the default branch in every direction switch.

const NONE = -1;

/**
 * Optimised rule engine.
 *
 * Every *cell* covered by an arrow is a node threaded into a doubly linked list along its
 * row and along its column, in ascending coordinate order. Because occupancy only ever
 * shrinks, the first obstacle in the head's exit corridor is exactly the neighbour link of
 * the head cell:
 *
 *   right -> nextInRow, left -> prevInRow, down -> nextInCol, up -> prevInCol
 *
 * So legality is a single array lookup regardless of how long the arrow or the corridor is,
 * and it naturally reports an arrow's own tail as a blocker when its path loops in front of
 * its head. Removing an arrow unlinks its cells in O(path length) and can only affect the
 * handful of arrows whose heads pointed at those cells, so the removable set is maintained
 * incrementally. Equivalence with `clearance.ts` is enforced by tests, including exhaustive
 * small-board sweeps.
 */
export class BoardIndex {
  readonly arrowCount: number;

  /** Per-cell-node state. Nodes are numbered by (arrow, position along its path). */
  private readonly cellX: Int32Array;
  private readonly cellY: Int32Array;
  private readonly cellArrow: Int32Array;
  private readonly prevInRow: Int32Array;
  private readonly nextInRow: Int32Array;
  private readonly prevInCol: Int32Array;
  private readonly nextInCol: Int32Array;
  private readonly cellAlive: Uint8Array;

  /** Per-arrow state. */
  private readonly dir: Uint8Array;
  private readonly headNode: Int32Array;
  private readonly firstNode: Int32Array;
  private readonly nodeCount: Int32Array;
  private readonly alive: Uint8Array;
  private readonly removable: Uint8Array;

  private aliveCountValue: number;
  private removableCountValue: number;

  private constructor(arrowCount: number, cellCount: number) {
    this.arrowCount = arrowCount;
    this.cellX = new Int32Array(cellCount);
    this.cellY = new Int32Array(cellCount);
    this.cellArrow = new Int32Array(cellCount);
    this.prevInRow = new Int32Array(cellCount).fill(NONE);
    this.nextInRow = new Int32Array(cellCount).fill(NONE);
    this.prevInCol = new Int32Array(cellCount).fill(NONE);
    this.nextInCol = new Int32Array(cellCount).fill(NONE);
    this.cellAlive = new Uint8Array(cellCount).fill(1);

    this.dir = new Uint8Array(arrowCount);
    this.headNode = new Int32Array(arrowCount).fill(NONE);
    this.firstNode = new Int32Array(arrowCount).fill(NONE);
    this.nodeCount = new Int32Array(arrowCount);
    this.alive = new Uint8Array(arrowCount).fill(1);
    this.removable = new Uint8Array(arrowCount);

    this.aliveCountValue = arrowCount;
    this.removableCountValue = 0;
  }

  static fromBoard(board: BoardData): BoardIndex {
    let cellCount = 0;
    for (const arrow of board.arrows) cellCount += arrow.cells.length;

    const index = new BoardIndex(board.arrows.length, cellCount);

    // Lay out nodes arrow by arrow so an arrow's nodes are contiguous.
    let node = 0;
    for (const arrow of board.arrows) {
      index.dir[arrow.id] = DIRECTION_INDEX[arrow.direction];
      index.firstNode[arrow.id] = node;
      index.nodeCount[arrow.id] = arrow.cells.length;

      const head = arrowHead(arrow);
      for (const cell of arrow.cells) {
        index.cellX[node] = cell.x;
        index.cellY[node] = cell.y;
        index.cellArrow[node] = arrow.id;
        if (cell.x === head.x && cell.y === head.y) index.headNode[arrow.id] = node;
        node += 1;
      }
    }

    // Thread the row and column lists by sorting node ids on each axis.
    const byRow = new Map<number, number[]>();
    const byCol = new Map<number, number[]>();
    for (let i = 0; i < cellCount; i += 1) {
      const row = byRow.get(index.cellY[i]);
      if (row === undefined) byRow.set(index.cellY[i], [i]);
      else row.push(i);

      const column = byCol.get(index.cellX[i]);
      if (column === undefined) byCol.set(index.cellX[i], [i]);
      else column.push(i);
    }

    for (const nodes of byRow.values()) {
      nodes.sort((a, b) => index.cellX[a] - index.cellX[b]);
      for (let i = 0; i < nodes.length; i += 1) {
        index.prevInRow[nodes[i]] = i > 0 ? nodes[i - 1] : NONE;
        index.nextInRow[nodes[i]] = i + 1 < nodes.length ? nodes[i + 1] : NONE;
      }
    }
    for (const nodes of byCol.values()) {
      nodes.sort((a, b) => index.cellY[a] - index.cellY[b]);
      for (let i = 0; i < nodes.length; i += 1) {
        index.prevInCol[nodes[i]] = i > 0 ? nodes[i - 1] : NONE;
        index.nextInCol[nodes[i]] = i + 1 < nodes.length ? nodes[i + 1] : NONE;
      }
    }

    for (let id = 0; id < index.arrowCount; id += 1) {
      if (index.computeRemovable(id)) {
        index.removable[id] = 1;
        index.removableCountValue += 1;
      }
    }
    return index;
  }

  /** First occupied node in the head's exit corridor, or -1. */
  private blockerNode(id: number): number {
    const head = this.headNode[id];
    if (head === NONE) return NONE;
    switch (this.dir[id]) {
      case DIR_UP:
        return this.prevInCol[head];
      case DIR_RIGHT:
        return this.nextInRow[head];
      case DIR_DOWN:
        return this.nextInCol[head];
      default:
        return this.prevInRow[head];
    }
  }

  private computeRemovable(id: number): boolean {
    return this.blockerNode(id) === NONE;
  }

  get aliveCount(): number {
    return this.aliveCountValue;
  }

  get removableCount(): number {
    return this.removableCountValue;
  }

  get isCleared(): boolean {
    return this.aliveCountValue === 0;
  }

  isAlive(id: number): boolean {
    return this.alive[id] === 1;
  }

  isRemovable(id: number): boolean {
    return this.alive[id] === 1 && this.removable[id] === 1;
  }

  /** Arrow blocking this one, or -1. May be the arrow itself when its path loops ahead. */
  nearestBlocker(id: number): number {
    if (this.alive[id] !== 1) return NONE;
    const node = this.blockerNode(id);
    return node === NONE ? NONE : this.cellArrow[node];
  }

  /** Ascending arrow ids, so callers get a deterministic ordering without sorting. */
  removableIds(): number[] {
    const ids: number[] = [];
    for (let id = 0; id < this.arrowCount; id += 1) {
      if (this.alive[id] === 1 && this.removable[id] === 1) ids.push(id);
    }
    return ids;
  }

  /** Lowest removable arrow id, or -1. This is the canonical solver's choice function. */
  firstRemovableId(): number {
    for (let id = 0; id < this.arrowCount; id += 1) {
      if (this.alive[id] === 1 && this.removable[id] === 1) return id;
    }
    return NONE;
  }

  aliveIds(): number[] {
    const ids: number[] = [];
    for (let id = 0; id < this.arrowCount; id += 1) {
      if (this.alive[id] === 1) ids.push(id);
    }
    return ids;
  }

  /**
   * Unlinks a removable arrow.
   * @returns ids that became removable as a direct result, ascending.
   * @throws when the arrow is already removed or is blocked.
   */
  remove(id: number): number[] {
    if (id < 0 || id >= this.arrowCount) throw new RangeError(`Unknown arrow id ${id}`);
    if (this.alive[id] !== 1) throw new Error(`Arrow ${id} has already been removed`);
    if (this.removable[id] !== 1) throw new Error(`Arrow ${id} is blocked`);

    const first = this.firstNode[id];
    const count = this.nodeCount[id];
    const candidates: number[] = [];

    for (let offset = 0; offset < count; offset += 1) {
      const node = first + offset;
      const prevRow = this.prevInRow[node];
      const nextRow = this.nextInRow[node];
      const prevCol = this.prevInCol[node];
      const nextCol = this.nextInCol[node];

      if (prevRow !== NONE) this.nextInRow[prevRow] = nextRow;
      if (nextRow !== NONE) this.prevInRow[nextRow] = prevRow;
      if (prevCol !== NONE) this.nextInCol[prevCol] = nextCol;
      if (nextCol !== NONE) this.prevInCol[nextCol] = prevCol;

      this.cellAlive[node] = 0;

      // Only arrows whose head sat next to a freed cell can have changed state.
      for (const neighbour of [prevRow, nextRow, prevCol, nextCol]) {
        if (neighbour === NONE || this.cellAlive[neighbour] !== 1) continue;
        const owner = this.cellArrow[neighbour];
        if (owner !== id && this.alive[owner] === 1) candidates.push(owner);
      }
    }

    this.alive[id] = 0;
    this.removable[id] = 0;
    this.aliveCountValue -= 1;
    this.removableCountValue -= 1;

    const unlocked: number[] = [];
    for (const candidate of candidates) {
      if (this.alive[candidate] !== 1 || this.removable[candidate] === 1) continue;
      if (this.computeRemovable(candidate)) {
        this.removable[candidate] = 1;
        this.removableCountValue += 1;
        unlocked.push(candidate);
      }
    }
    unlocked.sort((a, b) => a - b);
    return unlocked;
  }

  directionOf(id: number): Direction {
    switch (this.dir[id]) {
      case DIR_UP:
        return 'up';
      case DIR_RIGHT:
        return 'right';
      case DIR_DOWN:
        return 'down';
      default:
        return 'left';
    }
  }

  clone(): BoardIndex {
    const copy = new BoardIndex(this.arrowCount, this.cellX.length);
    copy.cellX.set(this.cellX);
    copy.cellY.set(this.cellY);
    copy.cellArrow.set(this.cellArrow);
    copy.prevInRow.set(this.prevInRow);
    copy.nextInRow.set(this.nextInRow);
    copy.prevInCol.set(this.prevInCol);
    copy.nextInCol.set(this.nextInCol);
    copy.cellAlive.set(this.cellAlive);
    copy.dir.set(this.dir);
    copy.headNode.set(this.headNode);
    copy.firstNode.set(this.firstNode);
    copy.nodeCount.set(this.nodeCount);
    copy.alive.set(this.alive);
    copy.removable.set(this.removable);
    copy.aliveCountValue = this.aliveCountValue;
    copy.removableCountValue = this.removableCountValue;
    return copy;
  }

  /** Snapshot of alive flags, for the reference implementation and for tests. */
  aliveFlags(): Uint8Array {
    return Uint8Array.from(this.alive);
  }
}
